/**
 * @providesModule FTUETooltipV2
 */

import React, {
  useEffect,
  useState,
  useRef,
  MutableRefObject,
  cloneElement,
  ReactElement,
  useMemo,
  useCallback,
} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ViewStyle,
  TextStyle,
  Image,
  findNodeHandle,
  UIManager,
  Dimensions,
  Animated,
  Easing,
  Modal,
  StatusBar,
  Platform,
  StyleSheet,
} from 'react-native';
import styles from './FTUETooltip.style';
import Svg, {Defs, Mask, Rect} from 'react-native-svg';

type Position = 'above' | 'below' | 'auto';

interface ComponentLayout {
  x: number;
  y: number;
  width: number;
  height: number;
  pageX: number;
  pageY: number;
}

export interface FTUETooltipProps {
  /** Reference to the component this tooltip should point to */
  targetRef?: MutableRefObject<any>;
  /** Position relative to target component */
  position?: Position;
  /** Username to display in welcome message */
  username?: string;
  /** The description text to display in the tooltip */
  text?: string;
  /** Text for the Next button */
  buttonText?: string;
  /** Custom style for the container */
  containerStyle?: ViewStyle;
  /** Custom style for the welcome text */
  welcomeTextStyle?: TextStyle;
  /** Custom style for the description text */
  textStyle?: TextStyle;
  /** Custom style for the button */
  buttonStyle?: ViewStyle;
  /** Callback when Next button is pressed */
  onNextPress?: () => void;
  /** Callback when overlay is pressed (optional - for closing tooltip) */
  onOverlayPress?: () => void;
  /** Test ID for testing purposes */
  testID?: string;
  /** Whether to show the pointing hand image */
  showPointingHand?: boolean;
  /** Whether the tooltip is visible */
  visible?: boolean;
  /** Whether to show the full-screen overlay */
  showOverlay?: boolean;
  /** Whether to show scaled snapshot of target component */
  showTargetSnapshot?: boolean;
  /** Scale factor for target component snapshot */
  snapshotScale?: number;
  /** Optional: React element to render in the snapshot instead of cloning */
  targetComponent?: ReactElement;
}

export const TestIDs = {
  FTUE_TOOLTIP: 'ftue_tooltip',
  FTUE_TOOLTIP_TEXT: 'ftue_tooltip_text',
  FTUE_TOOLTIP_BUTTON: 'ftue_tooltip_button',
};

const OverlayWithRectCutout = ({
  cutoutX,
  cutoutY,
  cutoutWidth,
  cutoutHeight,
}: {
  cutoutX: number;
  cutoutY: number;
  cutoutWidth: number;
  cutoutHeight: number;
}) => (
  <View style={StyleSheet.absoluteFill}>
    <Svg height='100%' width='100%'>
      <Defs>
        <Mask id='mask'>
          {/* Entire mask area opaque */}
          <Rect x='0' y='0' width='100%' height='100%' fill='white' />
          {/* Cutout rectangle transparent, using props for position and size */}
          <Rect x={cutoutX} y={cutoutY} width={cutoutWidth} height={cutoutHeight} fill='black' />
        </Mask>
      </Defs>

      {/* Overlay rectangle with the mask applied */}
      <Rect
        x='0'
        y='0'
        width='100%'
        height='100%'
        fill='rgba(0, 0, 0, 0.6)' // semi-transparent black overlay
        mask='url(#mask)'
      />
    </Svg>
  </View>
);

export const FTUETooltipV2: React.FC<FTUETooltipProps> = ({
  targetRef,
  position = 'auto',
  username = '',
  text = 'Your gamer profile is ready! You can make changes anytime',
  buttonText = 'Next',
  containerStyle,
  welcomeTextStyle,
  textStyle,
  buttonStyle,
  onNextPress = () => {},
  onOverlayPress,
  testID = TestIDs.FTUE_TOOLTIP,
  showPointingHand = true,
  visible = true,
  showOverlay = true,
  showTargetSnapshot = true,
  snapshotScale = 1.1,
  targetComponent,
}) => {
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
    isAbove: boolean;
    isCalculated: boolean;
  }>({top: 0, left: 0, isAbove: false, isCalculated: false});
  const [tooltipWidth, setTooltipWidth] = useState<number>(0);
  const [handPosition, setHandPosition] = useState<{
    x: number;
    y: number;
    rotation: number;
  }>({x: 0, y: 0, rotation: 0});

  const [targetSnapshot, setTargetSnapshot] = useState<{
    layout: ComponentLayout | null;
    scale: number;
    element: ReactElement | null;
  }>({layout: null, scale: 1.1, element: null});

  // Animation value for hand pointer oscillation
  const handAnimation = useRef(new Animated.Value(0)).current;

  // Start hand pointer oscillation animation
  const startHandAnimation = () => {
    const oscillateAnimation = Animated.loop(
      Animated.sequence([
        Animated.timing(handAnimation, {
          toValue: 1,
          duration: 600, // Move forward in 800ms
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(handAnimation, {
          toValue: 0,
          duration: 600, // Move back in 800ms
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ])
    );

    oscillateAnimation.start();
    return oscillateAnimation;
  };

  const calculatePosition = (targetLayout: ComponentLayout, tooltipWidth: number) => {
    const screenHeight = Dimensions.get('window').height;
    const screenWidth = Dimensions.get('window').width;
    const tooltipHeight = 160; // Approximate tooltip height
    const verticalMargin = 20; // Increased margin for clear separation
    const horizontalMargin = 10;
    const handSpacing = showPointingHand ? 40 : 0; // Space for hand pointer

    // TODO: calculate status bar height here
    const statusBarHeight = 0;
    const overlayOffset = showOverlay ? statusBarHeight : 0;

    let isAbove = false;
    let top = 0;
    let left = 0;

    // When using overlay, coordinates need to account for Modal coordinate system
    const adjustedTargetY = showOverlay ? targetLayout.pageY - overlayOffset : targetLayout.pageY;
    const adjustedScreenHeight = showOverlay ? screenHeight - overlayOffset : screenHeight;

    // Determine if tooltip should be above or below
    if (position === 'above') {
      isAbove = true;
    } else if (position === 'below') {
      isAbove = false;
    } else {
      // Auto positioning - check available space with proper margins
      const spaceAbove = adjustedTargetY;
      const spaceBelow = adjustedScreenHeight - (adjustedTargetY + targetLayout.height);
      const requiredSpaceAbove = tooltipHeight + verticalMargin + handSpacing;

      isAbove = spaceAbove > spaceBelow && spaceAbove > requiredSpaceAbove;
    }

    // Calculate vertical position with clear separation
    if (isAbove) {
      // Position above target with margin and hand spacing
      top = adjustedTargetY - tooltipHeight - verticalMargin - handSpacing;
    } else {
      // Position below target with margin and hand spacing
      top = adjustedTargetY + targetLayout.height + verticalMargin + handSpacing + 60;
    }

    // Calculate horizontal position (center on target, but keep within screen bounds)
    const targetCenter = targetLayout.pageX + targetLayout.width / 2;
    left = targetCenter - tooltipWidth / 2;

    // Ensure tooltip stays within screen bounds
    if (left < horizontalMargin) {
      left = horizontalMargin;
    } else if (left + tooltipWidth > screenWidth - horizontalMargin) {
      left = screenWidth - tooltipWidth - horizontalMargin;
    }

    // Ensure tooltip doesn't go off screen vertically
    const minTop = overlayOffset + verticalMargin;
    const maxTop = adjustedScreenHeight - tooltipHeight - verticalMargin;

    if (top < minTop) {
      top = minTop;
    } else if (top > maxTop) {
      top = maxTop;
    }

    return {top, left, isAbove};
  };

  const calculateHandPosition = (
    targetLayout: ComponentLayout,
    tooltipTop: number,
    tooltipLeft: number,
    tooltipWidth: number,
    isAbove: boolean
  ) => {
    const tooltipHeight = 160;
    // Hand dimensions - adjust based on whether using emoji or images
    const handWidth = 80; // Using emoji size for now
    const handHeight = 80; // Using emoji size for now
    // Original image sizes: const handWidth = 61; const handHeight = 127;

    // Account for overlay coordinate system adjustment
    const statusBarHeight = StatusBar.currentHeight || 0;
    const overlayOffset = showOverlay ? statusBarHeight : 0;

    // Calculate target center (adjust for overlay if needed)
    const targetCenterX = targetLayout.pageX + targetLayout.width / 2;
    const targetCenterY = showOverlay
      ? targetLayout.pageY + targetLayout.height / 2 - overlayOffset
      : targetLayout.pageY + targetLayout.height / 2;

    // Calculate tooltip center
    const tooltipCenterX = tooltipLeft + tooltipWidth / 2;
    const tooltipCenterY = tooltipTop + tooltipHeight / 2;

    // Position hand to point towards target, considering the tooltip's position relative to target
    let handX = 0;
    let handY = 0;

    // Since tooltip is now positioned above/below with clear separation,
    // place hand on the side of tooltip closest to target
    if (isAbove) {
      // Tooltip is above target, place hand at bottom of tooltip pointing down
      handX = tooltipWidth / 2 - handWidth / 2;
      handY = tooltipHeight + 5; // Small offset from tooltip bottom
    } else {
      // Tooltip is below target, place hand at top of tooltip pointing up
      handX = tooltipWidth / 2 - handWidth / 2;
      handY = -handHeight - 5; // Small offset from tooltip top
    }

    // Calculate angle from hand position to target
    const handCenterX = handX + handWidth / 2;
    const handCenterY = handY + handHeight / 2;
    const handToTargetX = targetCenterX - (tooltipLeft + handCenterX);
    const handToTargetY = targetCenterY - (tooltipTop + handCenterY);
    let angle = Math.atan2(handToTargetY, handToTargetX) * (180 / Math.PI);

    // Adjust angle for hand's natural pointing direction
    // The emoji 👉 points right by default, so we need to adjust based on direction

    return {
      x: handX,
      y: handY,
      rotation: angle,
    };
  };

  const measureTarget = async () => {
    if (!targetRef?.current) return;

    const nodeHandle = findNodeHandle(targetRef.current);
    if (!nodeHandle) return;

    return new Promise<ComponentLayout>((resolve, reject) => {
      UIManager.measure(nodeHandle, (x, y, width, height, pageX, pageY) => {
        resolve({x, y, width, height, pageX, pageY});
      });
    });
  };

  useEffect(() => {
    if (visible && targetRef?.current) {
      measureTarget()
        .then((layout) => {
          if (layout) {
            const position = calculatePosition(layout, tooltipWidth);
            setTooltipPosition({...position, isCalculated: true});

            // Store target layout for snapshot if enabled
            if (showTargetSnapshot && showOverlay) {
              // Use provided target component or try to clone from ref
              let elementToRender = targetComponent || null;

              if (!elementToRender && targetRef?.current) {
                // For React Native refs, we typically can't clone the component directly
                // The ref usually points to the native component instance
                // We'll use a fallback approach or expect the targetComponent prop
                console.debug(
                  'Target ref found but no targetComponent prop provided. Using fallback rendering.'
                );
              }

              setTargetSnapshot({
                layout: layout,
                scale: snapshotScale,
                element: elementToRender,
              });
            }

            // Calculate hand position if showing pointing hand
            if (showPointingHand) {
              const handPos = calculateHandPosition(
                layout,
                position.top,
                position.left,
                tooltipWidth,
                position.isAbove
              );
              setHandPosition(handPos);

              // Start hand animation after position is set
              setTimeout(() => {
                startHandAnimation();
              }, 100);
            }
          }
        })
        .catch(console.error);
    }
  }, [
    visible,
    targetRef,
    tooltipWidth,
    position,
    showPointingHand,
    showTargetSnapshot,
    showOverlay,
    snapshotScale,
    targetComponent,
  ]);

  // Stop animation when component is hidden or unmounted
  useEffect(() => {
    if (!visible) {
      handAnimation.stopAnimation();
    }

    // Cleanup animation on unmount
    return () => {
      handAnimation.stopAnimation();
    };
  }, [visible, handAnimation]);

  const handleLayout = (event: any) => {
    const {width} = event.nativeEvent.layout;
    if (width !== tooltipWidth) {
      setTooltipWidth(width);
    }
  };

  // Calculate scaled component position
  const getScaledComponentStyles = () => {
    if (!targetSnapshot.layout) return {};

    const statusBarHeight = 0; // Using the hardcoded value from your code
    const overlayOffset = showOverlay ? statusBarHeight : 0;

    // Use original position and size - scaling will be handled by transform
    const adjustedTop = targetSnapshot.layout.pageY - overlayOffset;

    return {
      position: 'absolute' as const,
      left: targetSnapshot.layout.pageX,
      top: adjustedTop,
      width: targetSnapshot.layout.width,
      height: targetSnapshot.layout.height,
      zIndex: 1000,
    };
  };

  const dynamicStyles = {
    position: 'absolute' as const,
    top: tooltipPosition.top,
    left: tooltipPosition.left,
    zIndex: 1001, // Above the scaled component
  };

  const overlayStyles = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
  };

  // Create animated translations
  const animatedTranslateX = handAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 30],
  });

  const handStyles = {
    position: 'absolute' as const,
    left: handPosition.x,
    top: handPosition.y,
    transform: [{rotate: `${handPosition.rotation}deg`}, {translateX: animatedTranslateX}],
    zIndex: 1002, // Above tooltip and scaled component
  };

  const tooltipContent = (
    <View style={[styles.wrapper, dynamicStyles]} onLayout={handleLayout}>
      <View style={[styles.container, containerStyle]} testID={testID}>
        <View style={styles.textContainer}>
          {username && (
            <Text allowFontScaling={false} style={[styles.welcomeText, welcomeTextStyle]}>
              Welcome, {username}
            </Text>
          )}
          <Text
            allowFontScaling={false}
            style={[styles.tooltipText, textStyle]}
            testID={TestIDs.FTUE_TOOLTIP_TEXT}
          >
            {text}
          </Text>
        </View>

        <TouchableOpacity
          style={[styles.nextButton, buttonStyle]}
          onPress={onNextPress}
          activeOpacity={0.8}
          testID={TestIDs.FTUE_TOOLTIP_BUTTON}
        >
          <View style={styles.nextButtonContent}>
            <Text allowFontScaling={false} style={styles.nextButtonText}>
              {buttonText}
            </Text>
          </View>
          <View style={styles.nextButtonInnerShadow} />
        </TouchableOpacity>
      </View>

      {showPointingHand && (
        <Animated.View style={handStyles}>
          <Text
            allowFontScaling={false}
            style={[
              styles.handImage,
              {
                fontSize: 80,
                textAlign: 'center',
                lineHeight: 80,
                width: 80,
                height: 80,
              },
            ]}
          >
            👉
          </Text>
        </Animated.View>
      )}
    </View>
  );

  if (!visible || !tooltipPosition?.isCalculated) {
    return null;
  }

  if (showOverlay) {
    const scaledComponentStyles = getScaledComponentStyles();

    return (
      <View style={[StyleSheet.absoluteFillObject, {zIndex: 2}]}>
        {/* Full-screen overlay */}
        <TouchableOpacity style={overlayStyles} activeOpacity={1} onPress={onOverlayPress}>
          <OverlayWithRectCutout
            cutoutX={targetSnapshot?.layout?.pageX || 0}
            cutoutY={targetSnapshot?.layout?.pageY || 0}
            cutoutWidth={targetSnapshot?.layout?.width || 0}
            cutoutHeight={targetSnapshot?.layout?.height || 0}
          />
        </TouchableOpacity>

        {/* Scaled snapshot of target component */}
        {showTargetSnapshot && targetSnapshot.layout && (
          <Animated.View
            style={[
              scaledComponentStyles,
              {
                transform: [
                  {scale: targetSnapshot.scale},
                  // Add a subtle pulsing animation
                  {
                    scale: handAnimation.interpolate({
                      inputRange: [0, 1],
                      outputRange: [targetSnapshot.scale, targetSnapshot.scale * 1.02],
                    }),
                  },
                ],
              },
            ]}
          >
            {/* Visual representation of the target component */}
            <View
              style={{
                width: targetSnapshot.layout.width,
                height: targetSnapshot.layout.height,
                backgroundColor: 'rgba(255, 238, 124, 0.15)',
                borderRadius: 8,
                borderWidth: 2,
                borderColor: '#ffee7c',
                shadowColor: '#ffee7c',
                shadowOffset: {
                  width: 0,
                  height: 0,
                },
                shadowOpacity: 0.8,
                shadowRadius: 8,
                elevation: 8,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <View
                style={{
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                  borderRadius: 6,
                }}
              >
                {targetSnapshot.element}
              </View>
            </View>
          </Animated.View>
        )}

        {/* Tooltip content rendered above overlay */}
        {tooltipContent}
      </View>
    );
  }

  // Fallback to non-overlay mode
  return tooltipContent;
};

export const FTUETooptipV2Context = React.createContext({});

export const FTUETooptipV2ContextProvider = (props: {
  children: any;
  tooltipData: {
    text: string;
    username?: string;
    position?: Position;
    buttonText: string;
    id: string;
  }[];
}) => {
  // tooltipData id will be a key in the component reference
  const componentReferences = useRef({});

  const [tooltipDataIndex, setTooltipDataIndex] = useState(-1);

  const data = useMemo(() => {
    if (tooltipDataIndex === -1) {
      return false;
    }
    return props?.tooltipData?.[tooltipDataIndex];
  }, [tooltipDataIndex, props]);

  const memoizedChildren = useMemo(() => {
    return props?.children;
  }, []);

  const setComponentReference = useCallback((ref, index) => {
    if (!ref) {
      return;
    }

    let tempData = props?.tooltipData?.[index];
    componentReferences.current[tempData?.id] = ref;
    if (Object?.keys(componentReferences.current)?.length === props?.tooltipData?.length) {
      setTooltipDataIndex(0);
    }
  }, []);

  const _value = useMemo(() => {
    return {setComponentReference};
  }, []);
  return (
    <FTUETooptipV2Context.Provider value={_value}>
      {data && (
        <FTUETooltipV2
          buttonText={data?.buttonText}
          onNextPress={() => setTooltipDataIndex(tooltipDataIndex + 1)}
          testID='ftue_tooltip'
          text={data?.text}
          username={data?.username}
          targetRef={{current: componentReferences?.current?.[data?.id]}}
          position={data?.position}
          showPointingHand={true}
          visible={true}
          showOverlay={true}
        />
      )}
      {memoizedChildren}
    </FTUETooptipV2Context.Provider>
  );
};

export const withFTUETooltipProvider = (Component: any, data: any) => {
  return (props: any) => {
    return (
      <FTUETooptipV2ContextProvider tooltipData={data?.tooltipData}>
        <Component {...props} />
      </FTUETooptipV2ContextProvider>
    );
  };
};
